import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, TimePicker, DatePicker, Input, message, Card, Spin, Modal } from "antd";
import { ClockCircleOutlined, SaveOutlined } from "@ant-design/icons";
import moment from "moment";
import CreateTimesheetApi from "../../api/timesheets/CreateTimesheetApi";
import GetTimesheetsByPlanApi from "../../api/timesheets/GetTimesheetsByPlanApi";
import TimeTracker from "../common/TimeTracker";

const { TextArea } = Input;
function PlanWorksheet({ planId, data }) {
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [workDate, setWorkDate] = useState(moment());
  const [description, setDescription] = useState('');
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);

  const loadTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetTimesheetsByPlanApi(planId);
      if (response.success) {
        setTimesheets(response.data || []);
      } else {
        message.error(response.message || 'Failed to load timesheets');
      }
    } catch (error) {
      console.error('Error loading timesheets:', error);
      message.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    if (planId) {
      loadTimesheets();
    }

    // Listen for auto-saved timesheets from TimeTracker
    const handleTimesheetSaved = (data) => {
      if (data.planId === planId) {
        loadTimesheets(); // Refresh the list
      }
    };

    // Import TimeTrackingService and add listener
    import('../../services/TimeTrackingService').then(({ default: timeTrackingService }) => {
      timeTrackingService.on('timesheetSaved', handleTimesheetSaved);
    });

    return () => {
      // Cleanup listener
      import('../../services/TimeTrackingService').then(({ default: timeTrackingService }) => {
        timeTrackingService.off('timesheetSaved', handleTimesheetSaved);
      });
    };
  }, [planId, loadTimesheets]);

  const handleTimeSubmit = () => {
    if (!startTime || !endTime) {
      message.error('Please select both start and end times');
      return;
    }

    if (endTime.isSameOrBefore(startTime)) {
      message.error('End time must be after start time');
      return;
    }

    setConfirmModal(true);
  };

  const confirmTimesheet = async () => {
    setSubmitting(true);
    try {
      const response = await CreateTimesheetApi(
        planId,
        startTime.toISOString(),
        endTime.toISOString(),
        workDate.format('YYYY-MM-DD'),
        description
      );

      if (response.success) {
        message.success('Timesheet logged successfully!');
        setStartTime(null);
        setEndTime(null);
        setDescription('');
        setConfirmModal(false);
        loadTimesheets();
      } else {
        message.error(response.message || 'Failed to log timesheet');
      }
    } catch (error) {
      message.error('Failed to log timesheet');
    }
    setSubmitting(false);
  };

  const calculateHours = () => {
    if (startTime && endTime && endTime.isAfter(startTime)) {
      const duration = moment.duration(endTime.diff(startTime));
      return duration.asHours().toFixed(2);
    }
    return '0.00';
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'workDate',
      key: 'workDate',
      render: (date) => moment(date).format('MMM DD, YYYY'),
      sorter: (a, b) => moment(a.workDate).unix() - moment(b.workDate).unix(),
    },
    {
      title: 'Start Time',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (time) => moment(time).format('HH:mm'),
    },
    {
      title: 'End Time',
      dataIndex: 'endTime',
      key: 'endTime',
      render: (time) => moment(time).format('HH:mm'),
    },
    {
      title: 'Hours Worked',
      dataIndex: 'hoursWorked',
      key: 'hoursWorked',
      render: (hours) => {
        const numHours = parseFloat(hours) || 0;
        return `${numHours.toFixed(1)} hrs`;
      },
      width: 120,
    },
    {
      title: 'Payment',
      key: 'payment',
      render: (_, record) => {
        const hours = parseFloat(record.hoursWorked) || 0;
        const hourlyRate = parseFloat(record.user?.hourlyRate) || 0;
        const payment = hours * hourlyRate;
        
        if (hourlyRate > 0) {
          return (
            <div className="text-green-600 font-medium">
              ${payment.toFixed(2)}
            </div>
          );
        }
        return <span className="text-gray-400">No rate set</span>;
      },
      width: 100,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'User',
      key: 'user',
      render: (_, record) => {
        const user = record.user;
        console.log('PlanWorksheet - Rendering user for timesheet:', user);
        
        let displayName = 'Unknown User';
        if (user) {
          const firstName = user.firstName || '';
          const lastName = user.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          
          if (fullName) {
            displayName = fullName;
          } else if (user.username) {
            displayName = user.username;
          }
        }
        
        return displayName;
      },
    },
  ];

  const totalHours = timesheets.reduce((sum, timesheet) => {
    const hours = parseFloat(timesheet.hoursWorked) || 0;
    return sum + hours;
  }, 0);

  const totalPayment = timesheets.reduce((sum, timesheet) => {
    const hours = parseFloat(timesheet.hoursWorked) || 0;
    const hourlyRate = parseFloat(timesheet.user?.hourlyRate) || 0;
    return sum + (hours * hourlyRate);
  }, 0);

  return (
    <div className="p-4">
      <Card 
        title={
          <div className="flex justify-between items-center">
            <span>Log Work Time</span>
            <TimeTracker planId={planId} showTime={true} showStop={true} />
          </div>
        } 
        className="mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-2">Work Date</label>
            <DatePicker
              value={workDate}
              onChange={setWorkDate}
              format="YYYY-MM-DD"
              className="w-full"
              disabledDate={(current) => current && current > moment().endOf('day')}
            />
          </div>
          <div></div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Start Time</label>
            <TimePicker
              value={startTime}
              onChange={setStartTime}
              format="HH:mm"
              className="w-full"
              placeholder="Select start time"
              suffixIcon={<ClockCircleOutlined />}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">End Time</label>
            <TimePicker
              value={endTime}
              onChange={setEndTime}
              format="HH:mm"
              className="w-full"
              placeholder="Select end time"
              suffixIcon={<ClockCircleOutlined />}
            />
          </div>
        </div>

        {startTime && endTime && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <div className="text-sm text-blue-600">
              <strong>Duration:</strong> {calculateHours()} hours
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Description (Optional)</label>
          <TextArea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what you worked on..."
            rows={3}
            maxLength={500}
          />
        </div>

        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleTimeSubmit}
          disabled={!startTime || !endTime}
          className="w-full md:w-auto"
        >
          Log Time
        </Button>
      </Card>

      <Card title={
        <div className="flex justify-between items-center">
          <span>Timesheet History</span>
          <div className="text-sm">
            <span className="mr-4">Total: {totalHours.toFixed(1)} hrs</span>
            {totalPayment > 0 && (
              <span className="text-green-600 font-medium">
                Payment: ${totalPayment.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      }>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={timesheets}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} entries`,
            }}
            scroll={{ x: 800 }}
          />
        </Spin>
      </Card>

      <Modal
        title="Confirm Timesheet Entry"
        visible={confirmModal}
        onOk={confirmTimesheet}
        onCancel={() => setConfirmModal(false)}
        confirmLoading={submitting}
        okText="Yes, Log Time"
        cancelText="Cancel"
      >
        <div className="space-y-3">
          <p>Please confirm your timesheet entry:</p>
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div><strong>Date:</strong> {workDate.format('MMM DD, YYYY')}</div>
            <div><strong>Start Time:</strong> {startTime?.format('HH:mm')}</div>
            <div><strong>End Time:</strong> {endTime?.format('HH:mm')}</div>
            <div><strong>Duration:</strong> {calculateHours()} hours</div>
            {description && <div><strong>Description:</strong> {description}</div>}
          </div>
          <p className="text-sm text-gray-600">
            Are you sure you want to log this time entry?
          </p>
        </div>
      </Modal>
    </div>
  );
}

export default PlanWorksheet;
